import { useState } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import { GlowCard } from '../ui/GlowCard'

interface AgentData {
  configured: boolean
  agent?: {
    id: string
    status: string
    version: string | null
    hostname: string | null
    platform: string | null
    local_subnet: string | null
    last_heartbeat: string | null
    uptime_seconds: number | null
    active_sessions: number
    device_count: number
    online_devices: number
    rdp_available: number
  }
}

interface Props {
  siteId: string
  agent: AgentData | null
  onInstall: () => void
  onRefresh: () => void
}

function WifiIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1"/></svg>
}

function ServerIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
}

export function AgentStatusCard({ siteId, agent, onInstall, onRefresh }: Props) {
  const [scanning, setScanning] = useState(false)
  const [deactivating, setDeactivating] = useState(false)
  const { toast } = useToast()

  const triggerScan = async () => {
    setScanning(true)
    try {
      await api.post(`/sites/${siteId}/agent/scan`)
      toast('Device scan started', 'success')
    } catch {
      toast('Failed to start scan', 'error')
    }
    setScanning(false)
  }

  const deactivate = async () => {
    if (!confirm('Are you sure you want to deactivate the site agent?')) return
    setDeactivating(true)
    try {
      await api.delete(`/sites/${siteId}/agent`)
      toast('Agent deactivated', 'success')
      onRefresh()
    } catch {
      toast('Failed to deactivate agent', 'error')
    }
    setDeactivating(false)
  }

  if (!agent?.configured || !agent.agent) {
    return (
      <GlowCard style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 'var(--r-m)',
            background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ServerIcon size={20} style={{ color: 'var(--text-3)' }} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>Site Agent</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Not installed</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, margin: '0 0 16px' }}>
          Install the Amber Site Agent to enable remote access, device discovery, and network monitoring for this site.
        </p>
        <Button onClick={onInstall}>Install Site Agent</Button>
      </GlowCard>
    )
  }

  const a = agent.agent
  const isOnline = a.status === 'active' && a.last_heartbeat &&
    (Date.now() - new Date(a.last_heartbeat).getTime()) < 120000
  const statusColor = isOnline ? 'var(--ok)' : 'var(--err)'
  const statusLabel = isOnline ? 'Connected' : 'Disconnected'
  const lastSeenText = a.last_heartbeat ? formatRelativeTime(a.last_heartbeat) : 'Never'

  return (
    <GlowCard style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 'var(--r-m)',
            background: 'var(--accent-m)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WifiIcon size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>Site Agent</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 'var(--r-f)', fontSize: 11, fontWeight: 600,
                color: statusColor,
                background: isOnline ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
                {statusLabel}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              {a.hostname || 'Unknown host'} {a.version ? `v${a.version}` : ''}
            </div>
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 12, marginBottom: 20,
      }}>
        <StatItem label="Devices" value={`${a.online_devices}/${a.device_count}`} sub="online" />
        <StatItem label="Sessions" value={String(a.active_sessions)} sub="active" />
        <StatItem label="Uptime" value={a.uptime_seconds ? formatUptime(a.uptime_seconds) : '—'} sub="" />
        <StatItem label="Last Seen" value={lastSeenText} sub="" />
        {a.local_subnet && <StatItem label="Subnet" value={a.local_subnet} sub="" mono />}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button size="sm" onClick={triggerScan} loading={scanning}>Scan Devices</Button>
        <Button size="sm" variant="ghost" onClick={deactivate} loading={deactivating}>Deactivate</Button>
      </div>
    </GlowCard>
  )
}

function StatItem({ label, value, sub, mono }: { label: string; value: string; sub: string; mono?: boolean }) {
  return (
    <div style={{
      background: 'var(--bg-2)', borderRadius: 'var(--r-m)', padding: '10px 12px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      <div style={{
        fontSize: 14, fontWeight: 600, color: 'var(--text-1)',
        fontFamily: mono ? 'var(--mono)' : 'var(--font)',
      }}>
        {value} {sub && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-3)' }}>{sub}</span>}
      </div>
    </div>
  )
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function formatUptime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}
